import { call, put, takeLatest, select, all } from 'redux-saga/effects'
import {
  is,
  path,
  pipe,
  prop,
  pathOr,
  filter,
  whereEq,
  partition,
} from 'ramda'
import { hasLength, lessThan24Hours } from 'shared/helpers/functional'
import { authenticatedCall } from 'shared/helpers/sagas'
import * as types from 'store/project/projectTypes'
import API from 'store/project/projectApis'
import history from 'routes/history'


function generatePayloadForTimeSlotCancel (timeSlots, projectId) {
  const startsWithin24Hours = pipe(prop('startTime'), lessThan24Hours)
  const message = 'Your schedule interview has been cancelled, we apologize for any inconvenience.'
  const action = 'cancel'
  if (timeSlots.length > 1) {
    const [slotsWithin24Hours, remainingSlots] = partition(startsWithin24Hours, timeSlots)
    return {
      projectId,
      action,
      body: {
        withLessThanOneDay: {
          bcc: [''],
          message,
          slots: slotsWithin24Hours.map(slot => ({
            timeSlotId: slot.id,
            offerHonorarium: true,
          })),
        },
        withMoreThanOneDay: {
          bcc: [''],
          message,
          slots: remainingSlots.map(slot => ({
            timeSlotId: slot.id,
            offerHonorarium: false,
          })),
        },
      },
    }
  }

  return {
    projectId,
    timeSlotId: timeSlots[0].id,
    action,
    body: {
      bcc: [''],
      message,
      offerHonorarium: startsWithin24Hours(timeSlots[0]),
    },
  }
}


function* watchFetchCrowdableAttributes ({payload: {id, onComplete}}) {
  yield* authenticatedCall('FETCH_CROWDABLE_ATTRIBUTES', function* () {
    const { attributes } = yield call(API.fetchCrowdableAttributes, id)

    if (is(Function, onComplete)) {
      onComplete(attributes)
    }

    yield put({
      type: 'FETCH_CROWDABLE_ATTRIBUTES_SUCCEEDED',
      payload: {
        marketId: id,
        attributes,
      },
    })
  })
}

export function* watchAddObservers ({ payload }) {
  yield* authenticatedCall('ADD_OBSERVERS_TO_PROJECT', function* () {
    const projectId = yield select(path(['currentProject', 'id']))
    yield call(API.putObservers, payload, projectId)
    yield put({ type: 'ADD_OBSERVERS_TO_PROJECT_SUCCEEDED', payload })
  })
}

export function* watchFetchDashboardInfo ({ payload: projectId }) {
  yield* authenticatedCall('FETCH_ALL_DASHBOARD_INFO', function* () {
    const dashboardInfo = yield API.fetchAvailabilityAndTimeSlots(projectId)

    yield put({
      type: 'FETCH_ALL_DASHBOARD_INFO_SUCCEEDED',
      payload: {
        dashboardInfo,
      },
    })
  })
}

// TODO ICI-430: cut down complexity / redundancy in this generator
export function* watchFetchAllInfoRequested ({ payload: projectId }) {
  try {
    const getProjectResult = yield API.fetchProject(projectId)
    const project = getProjectResult.data
    const clientId = project.clientId
    const conferenceLink = yield call(API.getConferenceLinkDetails, project.id)
    const getShowWarning =  yield call(API.getAvModeratorsCount,  {id: project.id,sampleSize: project.sampleSize});
    const showWarningBanner = getShowWarning.data.displayWarning
    const userId= yield select(path(['user', 'userId']))
    const languages = yield call(API.getLanguageAndLocalizationData)
    const topicNameRequest = yield call(API.getTopicNameDetails, project.id )
    const topicNameDetails =  topicNameRequest.data.topicsInformation
    const canNotBeEdited = topicNameRequest.data.canNotBeEdited
    if (!clientId) return
    const [
      { surveys },
      dashboardInfoApiResult,
      { users },
    ] = yield all([
      API.fetchProjectSurveys,
     API.fetchAvailabilityAndTimeSlots,
     API.fetchUsersForProject,
    ].map(f => f(projectId)))
    // const [
    //   surveyCrowds,
    // ] = yield all([
    //   fetchSurveyCrowds,
    // ].map(f => f(surveys)))
    const dashboardInfo = dashboardInfoApiResult.data
    yield put({
      type: 'GET_LANG_LOC_SUCCESSED',
      payload: languages.data
      ,
    })
    yield put({
      type: 'FETCH_ALL_NECESSARY_PROJECT_INFO_SUCCEEDED',
      payload: {
        ...project,
        conferenceLinkData: conferenceLink.data,
        topicNameDetails,
        dashboardInfo,
        showWarningBanner,
        userId,
        canNotBeEdited,
      },
    })
 
  } catch (err) {
      console.log("Get projects info failed ",err)
      yield put ({type: 'FETCH_ALL_NECESSARY_PROJECT_INFO_FAILED', message: err})
      yield put({type: 'SHOW_MESSAGE', payload: {message: 'There was an error processing your request, please try again', className: 'copy-screeners-snackbar'}})
      yield put({type: 'HIDE_MESSAGE'})
  }
}

export function* watchPutModeratorOption ({ payload }) {
  yield* authenticatedCall('CHANGE_MODERATOR', function* () {
    const project = yield select(path(['currentProject']))
    yield call(API.putProject, project, { qualModeratorId: payload })
    yield put({ type: 'CHANGE_MODERATOR_SUCCEEDED', payload })
  })
}

export function* watchPostProjectInquiry ({payload}) {
  yield* authenticatedCall('POST_PROJECT_INQUIRY', function* () {
    yield call(API.postProjectInquiry, payload)
    yield put({ type: 'POST_PROJECT_INQUIRY_SUCCEEDED' })
    payload.history.push('/projects')
  })
}

export function* watchPutProjectInquiry ({payload}) {
  yield* authenticatedCall('PUT_PROJECT_INQUIRY', function* () {
    yield call(API.putProjectInquiry, payload)
    yield put({ type: 'PUT_PROJECT_INQUIRY_SUCCEEDED' })
    payload.history.push('/projects')
  })
}

export function* watchPutProjectStatus ({payload}) {
  yield* authenticatedCall('PAUSE_PROJECT', function* () {
    const project = yield select(path(['currentProject']))
    const surveyInField = yield select(path(['sidebar', 'activeSurveys']))
    if (hasLength(surveyInField)) yield put({ type: 'CLOSE_SURVEYS_REQUESTED', payload: project.surveys })
    const response = yield call(API.putProject, project, payload)
    yield put({ type: 'PUT_PROJECT_STATUS_SUCCEEDED', payload: response })
    if (response.projectStatusId === 1 && payload.history) {
      payload.history.push(`/projects/${response.id}/review`)
    } else if (response.projectStatusId === 2 && payload.history) payload.history.push(`/projects/${response.id}/dashboard`)
  })
}

export function* watchCompleteProject ({payload}) {
  yield* authenticatedCall('COMPLETE_PROJECT', function* () {
    const project = yield select(prop('currentProject'))
    const scheduledSlots = filter(whereEq({isPicked: true, completed: false, noShow: null}), pathOr([], ['timeSlots', 'timeSlots'], project))
    if (hasLength(scheduledSlots)) {
      const cancelPayload = yield call(generatePayloadForTimeSlotCancel, scheduledSlots, project.id)
      if (scheduledSlots.length > 1) {
        yield put({ type: 'SEND_MULTI_SESSION_RESCHEDULE_MESSAGE', payload: cancelPayload })
      } else {
        yield put({ type: 'SEND_SINGLE_SESSION_RESCHEDULE_MESSAGE', payload: cancelPayload })
      }
    }
    yield put({ type: 'PUT_PROJECT_STATUS_REQUESTED', payload: {projectStatusId: 4} })
    payload.history.push('/projects')
  })
}


export function* watchFetchMarketsWithNpi () {
  yield* authenticatedCall('FETCH_MARKETS_WITH_NPI', function* () {
    const { markets } = yield API.fetchMarketsWithNpi()

    yield put({
      type: 'FETCH_MARKETS_WITH_NPI_SUCCEEDED',
      payload: {
        markets: markets,
      },
    })
  })
}

export function* watchFetchProjectSurveysRequested () {
  yield* authenticatedCall('FETCH_PROJECT_SURVEYS', function* () {
    const projectId = yield select(path(['currentProject', 'id']))
    const { surveys } = yield call(API.fetchProjectSurveys, projectId)

    yield put({
      type: 'FETCH_PROJECT_SURVEYS_SUCCEEDED',
      payload: {
        surveys,
      },
    })
  })
}

export function* watchPostConferenceLinkDetails (payload) {
  try {
    const currentProject = yield select(path(['currentProject']))
    const response =  yield call(API.postConferenceLinkDetails,payload,currentProject);
    if ( response.status === 200 ) {
      yield put({type: 'POST_CONFERENCE_LINK_DETAILS_SUCCESSED', conferenceLink:payload.editedConferenceLink, editedMeetingInformationMap: currentProject.editedMeetingInformationMap });
      history.push(`/projects/${currentProject.id}/dashboard`)
      const message =
        payload.editedConferenceLink && payload.editedConferenceLink.length > 0
          ? "Conference Link and Meeting Information changes have been saved."
          : "Information saved without a conference link.";
      yield put({type: 'SHOW_MESSAGE', payload: {message: message, className: 'copy-screeners-snackbar'}})
      yield put({type: 'HIDE_MESSAGE'})
    } else {
      yield put({type: 'POST_CONFERENCE_LINK_DETAILS_FAILED'})
      yield put({type: 'SHOW_MESSAGE', payload: {message: 'There was an error processing your request, please try again', className: 'copy-screeners-snackbar'}})
      yield put({type: 'HIDE_MESSAGE'})
    }
  } catch (err) {
      console.log("Post conference link details failed: ",err);
      yield put({type: 'POST_CONFERENCE_LINK_DETAILS_FAILED', message: err.message})
      yield put({type: 'SHOW_MESSAGE', payload: {message: 'There was an error processing your request, please try again', className: 'copy-screeners-snackbar'}})
      yield put({type: 'HIDE_MESSAGE'})
    }
}

export function* watchEditConferenceLinkDetails ({payload : payload}) {
  try {
    const message =
      payload.conferenceLink && payload.conferenceLink.length > 0
        ? payload.projectInProgress === true
          ? "Conference link has been updated and invitations will be resent to all participants and moderators for any scheduled interviews."
          : "Conference Link and Meeting Information changes have been saved."
        : "Information saved without a conference link.";
    const response =  yield call(API.editConferenceLinkDetails,payload);
    if ( response.status === 200 ) {
      yield put({type: 'UPDATE_CONFERENCE_LINK_DETAILS_SUCCESSED', payload: payload});
      yield put({type: 'TOGGLE_EDIT_CONFERENCE_LINK_CONFIRMATION_MODAL', isOpen: false, editedConferenceLink:payload.conferenceLink, editedMeetingInformationMap:payload.editedMeetingInformationMap})
      history.push(`/projects/${payload.projectId}/dashboard`)
      yield put({type: 'SHOW_MESSAGE', payload: {message: message ,className: 'copy-screeners-snackbar',},})
      yield put({type: 'HIDE_MESSAGE'})
    } else {
      yield put({type: 'UPDATE_CONFERENCE_LINK_DETAILS_FAILED'})
      yield put({type: 'SHOW_MESSAGE', payload: {message: 'There was an error processing your request, please try again',className: 'copy-screeners-snackbar'}})
      yield put({type: 'HIDE_MESSAGE'})
    }
  } catch (err) {
      console.log("Update conference link details failed: ",err);
      yield put({type: 'UPDATE_CONFERENCE_LINK_DETAILS_FAILED', message: err.message})
      yield put({type: 'SHOW_MESSAGE', payload: {message: 'There was an error processing your request, please try again',className: 'copy-screeners-snackbar'}})
      yield put({type: 'HIDE_MESSAGE'})
    }
}

export function* watchEditInterviewBuffer ({payload : payload}) {
  try {
    const response =  yield call(API.editInterviewBuffer,payload);
    if ( response.status === 200 ) {
      yield put({type: 'UPDATE_INTERVIEW_BUFFER_SUCCESSED', payload});
      yield put({type: 'CLOSE_EDIT_INTERVIEW_OR_MODERATOR_BUFFER_MODAL'});
      yield put({type: 'SHOW_MESSAGE', payload: {message: `${payload.flag === "postScreeninBuffer" ? "Interview Time Buffer successfully updated." : "Moderator Time Buffer successfully updated."}`, className: 'copy-screeners-snackbar'},})
      yield put({type: 'HIDE_MESSAGE'})
    } else {
      yield put({type: 'UPDATE_INTERVIEW_BUFFER_FAILED'})
      yield put({type: 'SHOW_MESSAGE', payload: {message: 'There was an error processing your request, please try again', className: 'copy-screeners-snackbar'}})
      yield put({type: 'HIDE_MESSAGE'})
    }
  } catch (err) {
      console.log("Update project interview buffer details failed: ",err);
      yield put({type: 'UPDATE_INTERVIEW_BUFFER_FAILED', message: err.message})
      yield put({type: 'SHOW_MESSAGE', payload: {message: 'There was an error processing your request, please try again', className: 'copy-screeners-snackbar'}})
      yield put({type: 'HIDE_MESSAGE'})
    }
}

export function* watchOpenSchedulerLinkConfirmationModal ({payload : payload}) {
  
    yield put({type: 'OPEN_GENERATE_SCHEDULER_LINK_CONFIRMATION_MODAL'});
    yield put({type: 'CLOSE_GENERATE_SCHEDULER_LINK_MODAL'})

}

export function* watchGenerateSchedulerLink ({payload : payload}) {
  try {
    const currentProject = yield select(path(['currentProject']))
    
    // If no conference link record exists, create an empty one first
    if (!currentProject.showCopyLinkButton) {
      const conferenceLinkPayload = {
        editedConferenceLink: '',
        editedMeetingInformation: [['en_us', '']],
      }
      yield call(API.postConferenceLinkDetails, conferenceLinkPayload, currentProject)
      yield put({
        type: 'POST_CONFERENCE_LINK_DETAILS_SUCCESSED',
        conferenceLink: '',
        editedMeetingInformationMap: currentProject.editedMeetingInformationMap
      })
    }
    
    const response =  yield call(API.editProjectDetails,payload);
    if ( response.status === 200 ) {
      const userId= yield select(path(['user', 'userId']))
      yield put({type: 'UPDATE_PROJECT_INFO_SUCCESSED', userId});
      yield put({type: 'FETCH_ALL_NECESSARY_PROJECT_INFO_REQUESTED', payload: currentProject.id});
      yield put({type: 'CLOSE_GENERATE_SCHEDULER_LINK_MODAL'})
      yield put({type: 'CLOSE_CONFIRMATION_GENERATE_SCHEDULER_LINK_MODAL'})
      yield put({type: 'SHOW_MESSAGE', payload: {message: 'Scheduler link created.', className: 'copy-screeners-snackbar'}})
      yield put({type: 'HIDE_MESSAGE'})
    } else {
      yield put({type: 'UPDATE_PROJECT_INFO_FAILED'})
      yield put({type: 'SHOW_MESSAGE', payload: {message: 'There was an error processing your request, please try again'}})
      yield put({type: 'HIDE_MESSAGE'})
    }
  } catch (err) {
      console.log("Update proejct details failed: ",err);
      yield put({type: 'UPDATE_PROJECT_INFO_FAILED', message: err.message})
      yield put({type: 'SHOW_MESSAGE', payload: {message: 'There was an error processing your request, please try again'}})
      yield put({type: 'HIDE_MESSAGE'})
    }
}

export function* watchEditSurveyId ({payload : payload}) {
  try {
    const response =  yield call(API.editProjectDetails,payload);
    if ( response.status === 200 ) {
      yield put({type: 'UPDATE_PROJECT_SURVEY_ID_SUCCESSED', surveyId: payload.editedSurveyId});
      yield put({type: 'CLOSE_CONFIRM_EDIT_SURVEY_ID_MODAL'})
      yield put({type: 'SHOW_MESSAGE', payload: {message: 'Survey ID successfully updated.', className: 'copy-screeners-snackbar'}})
      yield put({type: 'HIDE_MESSAGE'})
    } else {
      yield put({type: 'UPDATE_PROJECT_SURVEY_ID_FAILED'})
      yield put({type: 'SHOW_MESSAGE', payload: {message: 'There was an error processing your request, please try again'}})
      yield put({type: 'HIDE_MESSAGE'})
    }
  } catch (err) {
      console.log("Update proejct survey id failed: ",err);
      yield put({type: 'UPDATE_PROJECT_SURVEY_ID_FAILED', message: err.message})
      yield put({type: 'SHOW_MESSAGE', payload: {message: 'There was an error processing your request, please try again'}})
      yield put({type: 'HIDE_MESSAGE'})
    }
}

export function* watchGetAvModeratorsCount ({payload : payload}) {
  try {
    const response =  yield call(API.getAvModeratorsCount,payload);
    if ( response.status === 200 ) {
      yield put({type: 'GET_AVAILABLE_MODERATORS_COUNT_SUCCESSED',payload: { avModCount: response.data.avModCount, 
        projectId: payload.id,
        sampleSize: payload.sampleSize,
        initialSampleSize: payload.initialSampleSize,
        showWarning:response.data.displayWarning,
        showWarningBanner: payload.showWarningBanner
      }});
    } else {
      yield put({type: 'GET_AVAILABLE_MODERATORS_COUNT_FAILED'})
      yield put({type: 'SHOW_MESSAGE', payload: {message: 'There was an error processing your request, please try again', className: 'copy-screeners-snackbar'}})
      yield put({type: 'HIDE_MESSAGE'})
    }
  } catch (err) {
      console.log("Get available moderators count failed ",err);
      yield put({type: 'GET_AVAILABLE_MODERATORS_COUNT_FAILED', message: err.message})
      yield put({type: 'SHOW_MESSAGE', payload: {message: 'There was an error processing your request, please try again',  className: 'copy-screeners-snackbar'}})
      yield put({type: 'HIDE_MESSAGE'})
    }
}

export function* watchUpdateProjectSampleSize ({payload : payload}) {
   try {
     const response =  yield call(API.updateProjectSampleSize,payload);
     if ( response.status === 200 ) {
       yield put({type: 'UPDATE_PROJECT_SAMPLE_SIZE_SUCCESSED',  payload: {sampleSize: payload.sampleSize, showWarningBanner: payload.showWarning}});
       yield put({type: 'CLOSE_EDIT_SAMPLE_SIZE_WARNING_MODAL'});
       yield put({type: 'SHOW_MESSAGE', payload: {message: 'Sample size successfully updated.', className: 'copy-screeners-snackbar'}})
       yield put({type: 'HIDE_MESSAGE'})
     } else {
       yield put({type: 'UPDATE_PROJECT_SAMPLE_SIZE_FAILED'})
       yield put({type: 'SHOW_MESSAGE', payload: {message: 'There was an error processing your request, please try again', className: 'copy-screeners-snackbar'}})
       yield put({type: 'HIDE_MESSAGE'})
     }
   } catch (err) {
       console.log("Update project sample size failed ",err);
       yield put({type: 'UPDATE_PROJECT_SAMPLE_SIZE_FAILED', message: err.message})
       yield put({type: 'SHOW_MESSAGE', payload: {message: 'There was an error processing your request, please try again',  className: 'copy-screeners-snackbar'}})
       yield put({type: 'HIDE_MESSAGE'})
     }
 }

export function* watchUpdateProjectModerators({ payload: payload }) {
  try {
    const projectId = yield select(path(['currentProject', 'id']))
    const moderatorsId = payload.selectedModerators.map(mod => mod.id)
    const response = yield call(API.updateProjectModerators, moderatorsId, projectId, payload.clientId);
    console.log("response in multi ", response)
    if (response.status === 200) {
      yield put({ type: 'UPDATE_PROJECT_MODERATORS_SUCCEEDED', payload: payload.selectedModerators });
      yield put({ type: 'TOGGLE_ADD_MODERATORS_MODAL' });
      yield put({ type: 'SHOW_MESSAGE', payload: { message: 'Moderators successfully updated.', className: 'copy-screeners-snackbar' } })
      yield put({ type: 'HIDE_MESSAGE' })
    } else {
      yield put({ type: 'UPDATE_PROJECT_MODERATORS_FAILED' })
      yield put({ type: 'SHOW_MESSAGE', payload: { message: 'There was an error processing your request, please try again', className: 'copy-screeners-snackbar' } })
      yield put({ type: 'HIDE_MESSAGE' })
    }
  } catch (err) {
    console.log("update project moderators failed ", err);
  
    if (err && err.response && err.response.status === 405) {
      yield put({ type: 'SHOW_MESSAGE', payload: { message: 'You can only add/remove moderators when the project is in defining status', className: 'copy-screeners-snackbar' } })
    } else if (err && err.response && err.response.status === 412 && err.response.data.Cause.error.messageKey === "hasInterviews") {
      yield put({ type: 'TOGGLE_ADD_MODERATORS_MODAL' });
      yield put({
        type: 'TOGGLE_UNABLE_TO_REMOVE_MOD_MODAL',
        payload: err.response.data.body,
        showUnableToRemoveModErrorMessage: false,
        flag: payload.flag,
        modifiedModerators: payload.selectedModerators,
        status: err.response.status, 
        defaultModerators: payload.defaultModerators,
        defaultModeratorsDetails: payload.defaultModeratorsDetails,
        messageKey: err.response.data.Cause.error.messageKey,
      });
    } else if (err && err.response && err.response.status === 412 && err.response.data.Cause.error.messageKey === "noAvailabilities") {
      yield put({ type: 'TOGGLE_ADD_MODERATORS_MODAL' });
      yield put({
        type: 'TOGGLE_UNABLE_TO_REMOVE_MOD_MODAL',
        showUnableToRemoveModErrorMessage: true,
        flag: payload.flag,
        modifiedModerators: payload.selectedModerators,
        defaultModerators: payload.defaultModerators,
        status: err.response.status,
        defaultModeratorsDetails: payload.defaultModeratorsDetails,
        messageKey: err.response.data.Cause.error.messageKey,
      });
    
    }else {
      yield put({ type: 'UPDATE_PROJECT_MODERATORS_FAILED', message: err.message })
      yield put({ type: 'SHOW_MESSAGE', payload: { message: 'There was an error processing your request, please try again', className: 'copy-screeners-snackbar' } })
      yield put({ type: 'HIDE_MESSAGE' })
    }
  
  }
}

export function* watchDeleteProjectModerator({ payload: payload }) {
  try {
    const projectId = yield select(path(['currentProject', 'id']))
    console.log("payload ", payload)
    console.log("project Id ", projectId)
    const response = yield call(API.deleteProjectModerator, payload.moderatorId, projectId, payload.clientId);
    console.log("response in single", response)
    if (response.status === 200) {
      yield put({ type: 'DELETE_PROJECT_MODERATOR_SUCCEEDED', payload: response.data.body });
    } else {
      yield put({ type: 'DELETE_PROJECT_MODERATOR_FAILED' })
      yield put({ type: 'SHOW_MESSAGE', payload: { message: 'There was an error processing your request, please try again', className: 'copy-screeners-snackbar' } })
      yield put({ type: 'HIDE_MESSAGE' })
    }
  } catch (err) {
    console.log("Delete moderator failed ", err);
    if (err && err.response && err.response.status === 405) {
      yield put({ type: 'SHOW_MESSAGE', payload: { message: 'You can only remove moderators when the project is in defining status', className: 'copy-screeners-snackbar' } })
    }  
     else if (err.response.status === 412 && err.response.data.Cause.error.messageKey === "hasInterviews") {
       yield put({ type: 'TOGGLE_UNABLE_TO_REMOVE_MOD_MODAL', payload: err.response.data.body, showUnableToRemoveModErrorMessage: false, flag: payload.flag, clientId:payload.clientId });
    } else if (err.response.status === 412 && err.response.data.Cause.error.messageKey === "noAvailabilities") {
      yield put({ type: 'TOGGLE_UNABLE_TO_REMOVE_MOD_MODAL', showUnableToRemoveModErrorMessage: true, flag: payload.flag });
    } else {
      yield put({ type: 'DELETE_PROJECT_MODERATOR_FAILED', message: err.message })
      yield put({ type: 'SHOW_MESSAGE', payload: { message: 'There was an error processing your request, please try again', className: 'copy-screeners-snackbar' } })
      yield put({ type: 'HIDE_MESSAGE' })
    }
  }
}

 export function* watchGetUnavailableModerators ({payload : payload}) {
  try {
    const response =  yield call(API.getUnavailableModerators,payload);
    if ( response.status === 200 ) {
      yield put({type: 'GET_UNAVAILABLE_MODERATORS_SUCCESSED',  payload: response.data});
     
      
      if (response.data.displayWarning || response.data.displayError) {
        yield put({type: 'OPEN_GENERATE_SCHEDULER_LINK_MODAL'});
      } else {
        //yield put({type: 'UPDATE_PROJECT_INFO_REQUESTED', payload : payload});
        yield put({type: 'OPEN_GENERATE_SCHEDULER_LINK_CONFIRMATION_MODAL',  payload: response.data});
      }
    } else {
      yield put({type: 'GET_UNAVAILABLE_MODERATORS_FAILED'})
      yield put({type: 'SHOW_MESSAGE', payload: {message: 'There was an error processing your request, please try again', className: 'copy-screeners-snackbar'}})
      yield put({type: 'HIDE_MESSAGE'})
    }
  } catch (err) {
      console.log("Get unavailable moderators failed ",err);
      yield put({type: 'GET_UNAVAILABLE_MODERATORS_FAILED', message: err.message})
      yield put({type: 'SHOW_MESSAGE', payload: {message: 'There was an error processing your request, please try again',  className: 'copy-screeners-snackbar'}})
      yield put({type: 'HIDE_MESSAGE'})
    }
}

export function* watchPutModeratorTimeRange ({payload : payload}) {
  try {
    const response =  yield call(API.putModeratorTimeRange,payload);
    if ( response.status === 200 ) {
      yield put({type: 'PUT_MODERATOR_TIME_RANGE_SUCCESSED',  payload: payload});
      yield put({type: 'TOGGLE_TIME_RANGE_MODAL', payload: payload})
    } else {
      yield put({type: 'PUT_MODERATOR_TIME_RANGE_FAILED'})
      yield put({type: 'SHOW_MESSAGE', payload: {message: 'There was an error processing your request, please try again', className: 'copy-screeners-snackbar'}})
      yield put({type: 'HIDE_MESSAGE'})
    }
  } catch (err) {
      console.log("Get unavailable moderators failed ",err);
      yield put({type: 'PUT_MODERATOR_TIME_RANGE_FAILED', message: err.message})
      yield put({type: 'SHOW_MESSAGE', payload: {message: 'There was an error processing your request, please try again',  className: 'copy-screeners-snackbar'}})
      yield put({type: 'HIDE_MESSAGE'})
    }
}

export function* watchDeleteTranslations({ payload: payload }) {
  try {
    const response = yield call(API.deleteTranslations, payload);
    if (response.status === 200) {
      yield put({ type: 'DELETE_TRANSLATIONS_SUCCESSED', payload: payload });
    } else {
      yield put({ type: 'DELETE_TRANSLATIONS_FAILED' })
      yield put({ type: 'SHOW_MESSAGE', payload: { message: 'There was an error processing your request, please try again', className: 'copy-screeners-snackbar' } })
      yield put({ type: 'HIDE_MESSAGE' })
    }
  } catch (err) {
    console.log("Delete translations failed ", err);
    yield put({ type: 'DELETE_TRANSLATIONS_FAILED', payload: payload });
    if (err.response.status === 500) {
      yield put({ type: 'TOGGLE_TRANSLATIONS_ERROR_MODAL', payload: payload })
    } else {
      yield put({ type: 'SHOW_MESSAGE', payload: { message: 'There was an error processing your request, please try again', className: 'copy-screeners-snackbar' } })
      yield put({ type: 'HIDE_MESSAGE' })
    }
  }
}

export function* watchDeleteTopicTranslations({ payload: payload }) {
  try {
    const response = yield call(API.deleteTopicTranslations, payload);
    if (response.status === 200) {
      yield put({ type: 'DELETE_TOPIC_TRANSLATIONS_SUCCESSED', payload: payload });
    } else {
      yield put({ type: 'DELETE_TOPIC_TRANSLATIONS_FAILED' })
      yield put({ type: 'SHOW_MESSAGE', payload: { message: 'There was an error processing your request, please try again', className: 'copy-screeners-snackbar' } })
      yield put({ type: 'HIDE_MESSAGE' })
    }
  } catch (err) {
    console.log("Delete topic translations failed ", err);
    yield put({ type: 'DELETE_TOPIC_TRANSLATIONS_FAILED', payload: payload });
    if (err.response.status === 500) {
      yield put({ type: 'TOGGLE_TOPIC_TRANSLATIONS_ERROR_MODAL', payload: payload })
    } else {
      yield put({ type: 'SHOW_MESSAGE', payload: { message: 'There was an error processing your request, please try again', className: 'copy-screeners-snackbar' } })
      yield put({ type: 'HIDE_MESSAGE' })
    }
  }
}


export function* wacthAddTopicNameTranslations({ payload: payload }) {
  try {
    const response = yield call(API.addTopicNameTranslations, payload);
    if (response.status === 200) {
      yield put({ type: 'ADD_TOPIC_NAME_TRANSLATIONS_SUCCESSED', payload: payload });
      history.push(`/projects/${payload.id}/dashboard`)
    } else {
      yield put({ type: 'ADD_TOPIC_NAME_TRANSLATIONS_FAILED' })
      yield put({ type: 'SHOW_MESSAGE', payload: { message: 'There was an error processing your request, please try again', className: 'copy-screeners-snackbar' } })
      yield put({ type: 'HIDE_MESSAGE' })
    }
  } catch (err) {
    console.log("add topic name translations failed ", err);
    yield put({ type: 'ADD_TOPIC_NAME_TRANSLATIONS_FAILED', payload: payload });
    yield put({ type: 'SHOW_MESSAGE', payload: { message: 'There was an error processing your request, please try again', className: 'copy-screeners-snackbar' } })
    yield put({ type: 'HIDE_MESSAGE' })
  }
}

export default function* watchProjectSaga () {
  yield takeLatest(types.FETCH_ALL_NECESSARY_PROJECT_INFO_REQUESTED, watchFetchAllInfoRequested)
  yield takeLatest(types.CHANGE_MODERATOR_REQUESTED, watchPutModeratorOption)
  yield takeLatest(types.ADD_OBSERVERS_TO_PROJECT_REQUESTED, watchAddObservers)
  yield takeLatest(types.POST_PROJECT_INQUIRY_REQUESTED, watchPostProjectInquiry)
  yield takeLatest(types.PUT_PROJECT_INQUIRY_REQUESTED, watchPutProjectInquiry)
  yield takeLatest(types.FETCH_CROWDABLE_ATTRIBUTES_REQUESTED, watchFetchCrowdableAttributes)
  yield takeLatest(types.PUT_PROJECT_STATUS_REQUESTED, watchPutProjectStatus)
  yield takeLatest(types.COMPLETE_PROJECT_REQUESTED, watchCompleteProject)
  yield takeLatest(types.FETCH_ALL_DASHBOARD_INFO_REQUESTED, watchFetchDashboardInfo)
  yield takeLatest(types.FETCH_MARKETS_WITH_NPI_REQUESTED, watchFetchMarketsWithNpi)
  yield takeLatest(types.FETCH_PROJECT_SURVEYS_REQUESTED, watchFetchProjectSurveysRequested)
  yield takeLatest(types.POST_CONFERENCE_LINK_DETAILS_REQUESTED, watchPostConferenceLinkDetails)
  yield takeLatest(types.UPDATE_CONFERENCE_LINK_DETAILS_REQUESTED, watchEditConferenceLinkDetails)
  yield takeLatest(types.UPDATE_PROJECT_INFO_REQUESTED, watchGenerateSchedulerLink)
  yield takeLatest(types.OPEN_SCHEDULER_LINK_CONFIRMATION_MODAL, watchOpenSchedulerLinkConfirmationModal) 
  yield takeLatest(types.UPDATE_PROJECT_SURVEY_ID_REQUESTED,  watchEditSurveyId)
  yield takeLatest(types.GET_AVAILABLE_MODERATORS_COUNT_REQUESTED, watchGetAvModeratorsCount)
  yield takeLatest(types.UPDATE_PROJECT_SAMPLE_SIZE_REQUESTED, watchUpdateProjectSampleSize)
  yield takeLatest(types.UPDATE_PROJECT_MODERATORS_REQUESTED, watchUpdateProjectModerators)
  yield takeLatest(types.DELETE_PROJECT_MODERATOR_REQUESTED, watchDeleteProjectModerator)
  yield takeLatest(types.GET_UNAVAILABLE_MODERATORS_REQUESTED, watchGetUnavailableModerators)
  yield takeLatest(types.PUT_MODERATOR_TIME_RANGE_RQUESTED, watchPutModeratorTimeRange)
  yield takeLatest(types.UPDATE_INTERVIEW_BUFFER_REQUESTED, watchEditInterviewBuffer)
  yield takeLatest(types.DELETE_TRANSLATIONS_REQUESTED, watchDeleteTranslations)
  yield takeLatest(types.DELETE_TOPIC_TRANSLATIONS_REQUESTED, watchDeleteTopicTranslations)
  yield takeLatest(types.ADD_TOPIC_NAME_TRANSLATIONS_REQUESTSED, wacthAddTopicNameTranslations)
}
